import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Input, Button, Badge, Spinner } from '../atoms';
import { Modal } from './Modal';
import { useToast } from './Toast';

/**
 * SavingsEmailModal — Domain 08.8 operator surface for the approval-gated
 * "good news" savings email.
 *
 * Drives the REAL savings routes (routes/savings.js, mounted at /api/appeals):
 *   GET  /api/appeals/:id/savings-email/preview            computed figures
 *   POST /api/appeals/:id/savings-email/draft              create UNAPPROVED draft
 *   PUT  /api/appeals/:id/savings-email/:outboxId          edit subject/body
 *   POST /api/appeals/:id/savings-email/:outboxId/approve  approve → keystone flush sends it
 *   POST /api/appeals/:id/savings-email/:outboxId/reject   status='skipped', never sends
 *
 * MONEY-BLIND: the response-boundary guard strips the computed figure keys and
 * the whole `savings` object for the money-blind `administrator`, and the route
 * omits the rendered subject/body for that role. This component renders whatever
 * the server returns — so a money-blind operator sees the draft exists and can
 * approve/reject it, but never the reduction / tax-savings dollars or the money
 * that the email body interpolates. Nothing here re-derives money client-side.
 */

interface SavingsPreview {
  appealId: string;
  parcelId: string;
  taxYear: number | null;
  parcel: { account_number?: string | null; name?: string | null };
  client: { id: string; name: string } | null;
  // Money figures — ABSENT for the money-blind role (stripped at the boundary).
  priorValue?: number;
  finalValue?: number;
  reductionValue?: number;
  taxRate?: number;
  taxSavings?: number;
  recipientEmail: string | null;
}

interface SavingsDraft {
  outbox_id: string;
  to: string[];
  draft_kind: string;
  approved: boolean;
  status: string;
  // Rendered content — ABSENT for the money-blind role.
  subject?: string;
  body_html?: string;
  body_text?: string;
}

export interface SavingsEmailModalProps {
  appealId: string;
  open: boolean;
  onClose: () => void;
}

/** Money helper — matches the app convention ($X,XXX). Figures are dollars. */
function usd(n: number | undefined): string {
  return n == null ? '—' : `$${n.toLocaleString()}`;
}

/** Plain text → simple HTML body (blank-line paragraphs, single newlines → <br>). */
function textToHtml(text: string): string {
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return text
    .split(/\n{2,}/)
    .map(p => `<p>${esc(p).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

function errMessage(err: unknown): string {
  if (err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    return String((err.body as { error: unknown }).error);
  }
  return err instanceof Error ? err.message : String(err);
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: 'var(--sp-1)',
  fontSize: 'var(--fs-sm)',
  fontWeight: 'var(--fw-medium)',
  color: 'var(--color-ink)',
};

export function SavingsEmailModal({ appealId, open, onClose }: SavingsEmailModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [draft, setDraft] = useState<SavingsDraft | null>(null);
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [dirty, setDirty] = useState(false);
  const [done, setDone] = useState<null | 'approved' | 'rejected'>(null);

  // Read-only figures for the review phase. Enabled only while the modal is open
  // and before a draft has been created.
  const previewQuery = useQuery<SavingsPreview>({
    queryKey: ['savings-preview', appealId],
    queryFn: ({ signal }) => api.get<SavingsPreview>(`/api/appeals/${appealId}/savings-email/preview`, signal),
    enabled: open && !draft && !done,
  });

  // Prefill the recipient from the resolved client email once the preview loads.
  useEffect(() => {
    if (previewQuery.data?.recipientEmail && !to) setTo(previewQuery.data.recipientEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewQuery.data?.recipientEmail]);

  // Reset transient state whenever the modal is closed.
  useEffect(() => {
    if (!open) {
      setDraft(null); setTo(''); setSubject(''); setBody(''); setDirty(false); setDone(null);
    }
  }, [open]);

  const blind = !!previewQuery.data && previewQuery.data.reductionValue === undefined
    && previewQuery.data.taxSavings === undefined;

  const draftMutation = useMutation<SavingsDraft, Error, void>({
    mutationFn: () => api.post<SavingsDraft>(`/api/appeals/${appealId}/savings-email/draft`, to ? { to: [to] } : {}),
    onSuccess: (d) => {
      setDraft(d);
      setSubject(d.subject ?? '');
      setBody(d.body_text ?? '');
      setDirty(false);
    },
    onError: (err) => toast({ variant: 'error', message: errMessage(err) }),
  });

  const editMutation = useMutation<SavingsDraft, Error, void>({
    mutationFn: () => api.put<SavingsDraft>(
      `/api/appeals/${appealId}/savings-email/${draft!.outbox_id}`,
      { subject, body_text: body, body_html: textToHtml(body) },
    ),
    onSuccess: (d) => {
      setDraft(d);
      setDirty(false);
      toast({ variant: 'success', message: 'Draft saved.' });
    },
    onError: (err) => toast({ variant: 'error', message: errMessage(err) }),
  });

  const approveMutation = useMutation<{ approved: boolean }, Error, void>({
    mutationFn: () => api.post(`/api/appeals/${appealId}/savings-email/${draft!.outbox_id}/approve`, {}),
    onSuccess: () => {
      setDone('approved');
      queryClient.invalidateQueries({ queryKey: ['appeal-activity', appealId] });
      toast({ variant: 'success', message: 'Savings email approved and sent.' });
    },
    onError: (err) => toast({ variant: 'error', message: errMessage(err) }),
  });

  const rejectMutation = useMutation<{ status: string }, Error, void>({
    mutationFn: () => api.post(`/api/appeals/${appealId}/savings-email/${draft!.outbox_id}/reject`, {}),
    onSuccess: () => {
      setDone('rejected');
      queryClient.invalidateQueries({ queryKey: ['appeal-activity', appealId] });
      toast({ variant: 'success', message: 'Savings draft rejected — it will not send.' });
    },
    onError: (err) => toast({ variant: 'error', message: errMessage(err) }),
  });

  const busy = draftMutation.isPending || editMutation.isPending
    || approveMutation.isPending || rejectMutation.isPending;

  const previewHtml = useMemo(() => (body ? textToHtml(body) : ''), [body]);

  function footer() {
    if (done) {
      return <Button variant="primary" onClick={onClose}>Close</Button>;
    }
    if (!draft) {
      return (
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            variant="primary"
            loading={draftMutation.isPending}
            disabled={busy || previewQuery.isLoading || !!previewQuery.error || (!blind && !to)}
            onClick={() => draftMutation.mutate()}
          >
            Create draft
          </Button>
        </>
      );
    }
    return (
      <>
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="danger"
          loading={rejectMutation.isPending}
          disabled={busy}
          onClick={() => rejectMutation.mutate()}
        >
          Reject
        </Button>
        <Button
          variant="primary"
          loading={approveMutation.isPending}
          disabled={busy || dirty}
          onClick={() => approveMutation.mutate()}
        >
          Approve &amp; send
        </Button>
      </>
    );
  }

  return (
    <Modal open={open} onClose={() => { if (!busy) onClose(); }} title="Savings email" size="lg" footer={footer()}>
      {done ? (
        <div style={{ padding: 'var(--sp-3) 0', color: 'var(--color-ink)' }}>
          {done === 'approved'
            ? 'The savings email was approved and handed to the send queue.'
            : 'The savings draft was rejected. It will not be sent.'}
        </div>
      ) : !draft ? (
        <ReviewPhase
          preview={previewQuery.data}
          loading={previewQuery.isLoading}
          error={previewQuery.error}
          blind={blind}
          to={to}
          onTo={setTo}
        />
      ) : (
        <DraftPhase
          draft={draft}
          blind={blind}
          subject={subject}
          body={body}
          previewHtml={previewHtml}
          dirty={dirty}
          saving={editMutation.isPending}
          onSubject={(v) => { setSubject(v); setDirty(true); }}
          onBody={(v) => { setBody(v); setDirty(true); }}
          onSave={() => editMutation.mutate()}
        />
      )}
    </Modal>
  );
}

function ReviewPhase({
  preview, loading, error, blind, to, onTo,
}: {
  preview: SavingsPreview | undefined;
  loading: boolean;
  error: unknown;
  blind: boolean;
  to: string;
  onTo: (v: string) => void;
}) {
  if (loading) {
    return <p style={{ color: 'var(--color-ink-3)', display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}><Spinner size="sm" /> Computing savings…</p>;
  }
  if (error) {
    const notFound = error instanceof ApiClientError && error.status === 404;
    return (
      <p style={{ margin: 0, color: 'var(--color-status-red)' }}>
        {notFound ? 'No savings figures are available for this appeal.' : errMessage(error)}
      </p>
    );
  }
  if (!preview) return null;

  const who = preview.parcel.name || preview.parcel.account_number || 'this property';

  return (
    <div>
      <p style={{ margin: '0 0 var(--sp-4)', color: 'var(--color-ink-3)', fontSize: 'var(--fs-md)' }}>
        Prepare the client "good news" email for <strong style={{ color: 'var(--color-ink)' }}>{who}</strong>
        {preview.taxYear ? ` (${preview.taxYear})` : ''}. It is created as an unapproved draft — nothing sends until you approve it.
      </p>

      {blind ? (
        <div style={{
          padding: 'var(--sp-3)',
          background: 'var(--color-muted-bg, var(--color-card))',
          border: '1px dashed var(--color-border-strong)',
          borderRadius: 'var(--r-md)',
          marginBottom: 'var(--sp-4)',
          color: 'var(--color-ink-3)',
          fontSize: 'var(--fs-sm)',
        }}>
          Savings figures are hidden for your role. You can still create, approve, or reject this email; the amounts are filled in for the client automatically.
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 'var(--sp-3)',
          marginBottom: 'var(--sp-4)',
        }}>
          <Figure label="Prior value" value={usd(preview.priorValue)} />
          <Figure label="Final value" value={usd(preview.finalValue)} />
          <Figure label="Reduction" value={usd(preview.reductionValue)} />
          <Figure label="Est. tax savings" value={usd(preview.taxSavings)} accent />
        </div>
      )}

      <label style={{ display: 'block' }}>
        <span style={labelStyle}>Recipient</span>
        <Input
          type="email"
          value={to}
          onChange={e => onTo(e.target.value)}
          placeholder="client@example.com"
        />
        {!preview.recipientEmail && (
          <span style={{ display: 'block', marginTop: 'var(--sp-1)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
            No client email on file — enter one to create the draft.
          </span>
        )}
      </label>
    </div>
  );
}

function Figure({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div style={{
      padding: 'var(--sp-3)',
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-md)',
    }}>
      <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>{label}</div>
      <div style={{
        fontSize: 'var(--fs-lg)',
        fontWeight: 'var(--fw-semibold)',
        color: accent ? 'var(--brand-primary)' : 'var(--color-ink)',
        marginTop: 'var(--sp-0-5)',
      }}>{value}</div>
    </div>
  );
}

function DraftPhase({
  draft, blind, subject, body, previewHtml, dirty, saving, onSubject, onBody, onSave,
}: {
  draft: SavingsDraft;
  blind: boolean;
  subject: string;
  body: string;
  previewHtml: string;
  dirty: boolean;
  saving: boolean;
  onSubject: (v: string) => void;
  onBody: (v: string) => void;
  onSave: () => void;
}) {
  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 'var(--sp-2)',
        marginBottom: 'var(--sp-3)', flexWrap: 'wrap',
      }}>
        <Badge variant={draft.approved ? 'success' : 'warning'}>
          {draft.approved ? 'Approved' : 'Unapproved draft'}
        </Badge>
        <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
          To: {draft.to.join(', ') || '—'}
        </span>
      </div>

      {blind ? (
        <div style={{
          padding: 'var(--sp-4)',
          background: 'var(--color-muted-bg, var(--color-card))',
          border: '1px dashed var(--color-border-strong)',
          borderRadius: 'var(--r-md)',
          color: 'var(--color-ink-3)',
          fontSize: 'var(--fs-md)',
        }}>
          The email content is hidden for your role because it contains savings amounts. The draft is ready — you can approve it to send it to the client, or reject it.
        </div>
      ) : (
        <>
          <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
            <span style={labelStyle}>Subject</span>
            <Input value={subject} onChange={e => onSubject(e.target.value)} />
          </label>

          <label style={{ display: 'block', marginBottom: 'var(--sp-2)' }}>
            <span style={labelStyle}>Body</span>
            <textarea
              value={body}
              onChange={e => onBody(e.target.value)}
              rows={7}
              style={{
                width: '100%',
                padding: 'var(--sp-2) var(--sp-3)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--r-md)',
                background: 'var(--color-card)',
                color: 'var(--color-ink)',
                fontSize: 'var(--fs-md)',
                fontFamily: 'inherit',
                resize: 'vertical',
                outline: 'none',
              }}
            />
          </label>

          <div style={{ marginBottom: 'var(--sp-3)' }}>
            <Button variant="secondary" size="sm" loading={saving} disabled={!dirty} onClick={onSave}>
              Save changes
            </Button>
            {dirty && (
              <span style={{ marginLeft: 'var(--sp-2)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
                Save before approving.
              </span>
            )}
          </div>

          <div style={{
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--r-md)',
            padding: 'var(--sp-3)',
            background: 'var(--color-card)',
          }}>
            <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)', marginBottom: 'var(--sp-2)' }}>Preview</div>
            <div style={{ color: 'var(--color-ink)', fontSize: 'var(--fs-md)' }} dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </div>
        </>
      )}
    </div>
  );
}
